/**
 * Accept Invitation Page (DC-894)
 *
 * Allows invited users to set their password and activate their account.
 */

import React, { useState, useEffect, FormEvent } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { BRANDING } from '../../config/branding';
import { useAuth, InvitationDetails } from '../../hooks/useAuth';
import { PasswordStrengthIndicator, isPasswordValid } from '../../components/auth/PasswordStrengthIndicator';
import '../../styles/auth.css';

export function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const _navigate = useNavigate();
  const { getInvitationDetails, acceptInvitation, isLoading } = useAuth();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const [invitationError, setInvitationError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Load invitation details on mount
  useEffect(() => {
    async function loadInvitation() {
      if (!token) {
        setInvitationError('Invalid invitation link');
        setLoadingInvitation(false);
        return;
      }

      const result = await getInvitationDetails(token);

      if (result.success && result.data) {
        setInvitation(result.data);
      } else {
        setInvitationError(result.error || 'Invalid invitation');
      }

      setLoadingInvitation(false);
    }

    loadInvitation();
  }, [token, getInvitationDetails]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Invalid invitation link');
      return;
    }

    if (!isPasswordValid(password)) {
      setError('Password does not meet the requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const result = await acceptInvitation(token, password);

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error || 'Failed to accept invitation');
    }
  };

  // Loading state
  if (loadingInvitation) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-logo">
              <div className="auth-logo-text">{BRANDING.productName}</div>
            </div>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="auth-button-spinner" style={{ margin: '0 auto 1rem' }} />
              <p style={{ color: 'var(--muted)' }}>Loading invitation details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid or expired invitation
  if (invitationError || !invitation) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-logo">
              <div className="auth-logo-text">{BRANDING.productName}</div>
            </div>

            <div className="auth-error" style={{ marginBottom: '1.5rem' }}>
              <span className="auth-error-icon">!</span>
              {invitationError || 'Invalid invitation'}
            </div>

            <p style={{ color: 'var(--muted)', textAlign: 'center', marginBottom: '1.5rem' }}>
              This invitation link is invalid, has expired, or has already been used.
              Please contact your administrator for a new invitation.
            </p>

            <Link
              to="/login"
              className="auth-button"
              style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-logo">
              <div className="auth-logo-text">{BRANDING.productName}</div>
            </div>

            <div className="auth-success" style={{ marginBottom: '1.5rem' }}>
              <span>&#10003;</span>
              Account activated successfully!
            </div>

            <h1 className="auth-title">{`Welcome to ${BRANDING.productName}`}</h1>

            <p style={{ color: 'var(--muted)', textAlign: 'center', marginBottom: '1.5rem' }}>
              Your account has been activated. You can now sign in with your email
              and password.
            </p>

            <Link
              to="/login"
              className="auth-button"
              style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {/* Logo */}
          <div className="auth-logo">
            <div className="auth-logo-text">{BRANDING.productName}</div>
          </div>

          <h1 className="auth-title">Accept Invitation</h1>
          <p className="auth-subtitle">
            You've been invited to join <strong>{invitation.organizationName}</strong>
          </p>

          {/* Invitation details */}
          <div
            style={{
              background: 'var(--bg-secondary)',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
            }}
          >
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Name:</span>
              <span style={{ marginLeft: '0.5rem', fontWeight: 500 }}>{invitation.fullName}</span>
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Email:</span>
              <span style={{ marginLeft: '0.5rem' }}>{invitation.email}</span>
            </div>
            <div>
              <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Role:</span>
              <span
                style={{
                  marginLeft: '0.5rem',
                  textTransform: 'capitalize',
                  fontWeight: 500,
                  color:
                    invitation.role === 'admin'
                      ? 'var(--danger)'
                      : invitation.role === 'analyst'
                        ? 'var(--primary)'
                        : 'var(--muted)',
                }}
              >
                {invitation.role}
              </span>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="auth-error">
              <span className="auth-error-icon">!</span>
              {error}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            {/* Password */}
            <div className="auth-field">
              <label htmlFor="password" className="auth-label">
                Create Password
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '\uD83D\uDC41' : '\uD83D\uDC41\u200D\uD83D\uDDE8'}
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
            </div>

            {/* Confirm Password */}
            <div className="auth-field">
              <label htmlFor="confirmPassword" className="auth-label">
                Confirm Password
              </label>
              <div className="auth-input-wrapper">
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  className={`auth-input ${password !== confirmPassword && confirmPassword ? 'error' : ''}`}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </div>
              {password !== confirmPassword && confirmPassword && (
                <div className="auth-field-error">Passwords do not match</div>
              )}
            </div>

            {/* Submit button */}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="auth-button-spinner" />
                  Activating Account...
                </>
              ) : (
                'Activate Account'
              )}
            </button>
          </form>

          {/* Links */}
          <div className="auth-links">
            <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              Already have an account?
            </span>{' '}
            <Link to="/login" className="auth-link">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcceptInvitation;
